import React, { useState, useEffect } from 'react';
import { Search, Package, CheckCircle, Truck, Clock, XCircle, ChefHat, Phone, AlertTriangle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackOrderByCode, getQualityClaimByTrackingCode, createQualityClaim } from '@/services/firestore';
import type { TrackedOrder } from '@/services/firestore';
import { storage } from '@/lib/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import type { OrderStatus, QualityClaim, QualityIssueType, ResolutionMethod } from '@/types';
import { ORDER_STATUS_LABELS, QUALITY_ISSUE_LABELS, QUALITY_CLAIM_LABELS } from '@/types';

const LAST_TRACKING_KEY = 'gv:lastTrackingCode';
const LAST_TRACKING_PHONE_KEY = 'gv:lastTrackingPhone';

const STATUS_STEPS: OrderStatus[] = [
  'pending',
  'confirmed',
  'preparing',
  'out_for_delivery',
  'delivered',
];

const STATUS_ICONS: Record<OrderStatus, React.ReactNode> = {
  pending: <Clock size={22} />,
  confirmed: <CheckCircle size={22} />,
  preparing: <ChefHat size={22} />,
  out_for_delivery: <Truck size={22} />,
  delivered: <CheckCircle size={22} />,
  cancelled: <XCircle size={22} />,
};

const STATUS_DESCRIPTIONS: Record<OrderStatus, string> = {
  pending: 'Your order has been received. We\'ll call you shortly.',
  confirmed: 'Your order is confirmed! We are preparing it for dispatch.',
  preparing: 'Your onions are being sorted and packaged.',
  out_for_delivery: 'Your order is on the way!',
  delivered: 'Delivered! Thank you for ordering with us.',
  cancelled: 'This order was cancelled. Please contact us for details.',
};

const TrackOrderPage: React.FC = () => {
  const [codeInput, setCodeInput] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [order, setOrder] = useState<TrackedOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [claim, setClaim] = useState<QualityClaim | null>(null);
  const [showClaimModal, setShowClaimModal] = useState(false);

  // Load any existing quality claim once a delivered order is shown
  useEffect(() => {
    if (order?.status === 'delivered') {
      getQualityClaimByTrackingCode(order.trackingCode).then(setClaim).catch(() => {});
    } else {
      setClaim(null);
    }
  }, [order?.trackingCode, order?.status]);

  // Auto-read code + phone from URL params, else fall back to localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let code = params.get('code') || '';
    let phone = params.get('phone') || '';
    if (!code) { try { code = localStorage.getItem(LAST_TRACKING_KEY) || ''; } catch { /* private mode */ } }
    if (!phone) { try { phone = localStorage.getItem(LAST_TRACKING_PHONE_KEY) || ''; } catch { /* private mode */ } }
    if (code) setCodeInput(code);
    if (phone) setPhoneInput(phone);
    if (code && phone) handleSearch(code, phone);
  }, []);

  // Tracking now requires BOTH the code and the order's phone (two-factor). The
  // lookup runs server-side (trackOrder callable); orders aren't client-readable.
  const handleSearch = async (code?: string, phone?: string) => {
    const searchCode = (code ?? codeInput).trim();
    const searchPhone = (phone ?? phoneInput).trim();
    if (!searchCode || searchPhone.replace(/\D/g, '').length < 9) return;
    setLoading(true);
    setError('');
    setSearched(true);

    try {
      const found = await trackOrderByCode(searchCode, searchPhone);
      setOrder(found);
      if (!found) {
        setError('No order found for that tracking code and phone number.');
        return;
      }
      try {
        localStorage.setItem(LAST_TRACKING_KEY, searchCode);
        localStorage.setItem(LAST_TRACKING_PHONE_KEY, searchPhone);
      } catch { /* ignore */ }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentStepIndex = order
    ? order.status === 'cancelled'
      ? -1
      : STATUS_STEPS.indexOf(order.status)
    : -1;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Brand header — aligned with LandingPage editorial theme */}
      <div className="bg-emerald-900 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-20 shadow-sm">
        <Link to="/" className="flex items-center gap-2.5 group">
          <img src="/brand/logo-icon.png" alt="Genvitungu" className="w-8 h-8 rounded-lg object-cover" />
          <div className="leading-none">
            <p className="font-extrabold text-lg">Genvitungu</p>
            <p className="text-emerald-200 text-[10px] uppercase tracking-widest mt-0.5">Track your order</p>
          </div>
        </Link>
        <a href="tel:+254790215267" className="flex items-center gap-1 bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors">
          <Phone size={14} /> Call
        </a>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8 space-y-6">
        {/* Search box */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Track Your Order</h2>
          <p className="text-sm text-gray-500 mb-4">Enter your tracking code and the phone number you ordered with.</p>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Tracking code (e.g. GV-xxxxxxxx)"
              value={codeInput}
              onChange={e => setCodeInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 outline-none font-mono text-lg tracking-wider"
            />
            <div className="flex gap-3">
              <input
                type="tel"
                placeholder="Phone number (07XX XXX XXX)"
                value={phoneInput}
                onChange={e => setPhoneInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 outline-none text-lg"
              />
              <button
                onClick={() => handleSearch()}
                disabled={loading || !codeInput.trim() || phoneInput.replace(/\D/g, '').length < 9}
                className="px-5 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white font-semibold rounded-xl flex items-center gap-2 transition-colors"
              >
                {loading ? '...' : <Search size={20} />}
              </button>
            </div>
          </div>
          {error && <p className="text-red-500 text-sm mt-3">{error}</p>}
        </div>

        {/* Order details */}
        {order && (
          <>
            {/* Status header */}
            <div className={`rounded-2xl p-5 text-white ${
              order.status === 'delivered' ? 'bg-green-600'
              : order.status === 'cancelled' ? 'bg-red-500'
              : order.status === 'out_for_delivery' ? 'bg-blue-600'
              : 'bg-orange-500'
            }`}>
              <div className="flex items-center gap-3 mb-2">
                {STATUS_ICONS[order.status]}
                <span className="text-xl font-bold">{ORDER_STATUS_LABELS[order.status]}</span>
              </div>
              <p className="text-white/80 text-sm">{STATUS_DESCRIPTIONS[order.status]}</p>
            </div>

            {/* Progress timeline */}
            {order.status !== 'cancelled' && (
              <div className="bg-white rounded-2xl shadow-sm p-5">
                <h3 className="font-semibold text-gray-800 mb-5">Delivery Progress</h3>
                <div className="relative">
                  {STATUS_STEPS.map((step, idx) => {
                    const isDone = idx <= currentStepIndex;
                    const isCurrent = idx === currentStepIndex;
                    return (
                      <div key={step} className="flex gap-4 mb-4 last:mb-0">
                        <div className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 ${
                            isDone
                              ? isCurrent ? 'bg-orange-500 text-white ring-4 ring-orange-100' : 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-400'
                          }`}>
                            {STATUS_ICONS[step]}
                          </div>
                          {idx < STATUS_STEPS.length - 1 && (
                            <div className={`w-0.5 flex-1 mt-1 ${idx < currentStepIndex ? 'bg-green-400' : 'bg-gray-200'}`} style={{ minHeight: '24px' }} />
                          )}
                        </div>
                        <div className="pt-2 pb-4">
                          <p className={`font-medium ${isDone ? 'text-gray-900' : 'text-gray-400'}`}>
                            {ORDER_STATUS_LABELS[step]}
                          </p>
                          {isCurrent && (
                            <p className="text-sm text-orange-600 mt-0.5">{STATUS_DESCRIPTIONS[step]}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Order Details */}
            <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Package size={18} className="text-orange-500" /> Order Details
              </h3>
              <div className="divide-y divide-gray-100">
                {[
                  { label: 'Tracking Code', value: order.trackingCode },
                  { label: 'Customer', value: order.customerName },
                  { label: 'Delivery to', value: order.deliveryLocation },
                  { label: 'House No.', value: order.houseNumber || '—' },
                  { label: 'Estate', value: order.estate || '—' },
                  { label: 'Total Weight', value: `${order.totalWeightKg} kg` },
                  ...(order.estimatedTotal ? [{ label: 'Goods Subtotal', value: `KSh ${order.estimatedTotal.toLocaleString()}` }] : []),
                  ...(order.deliveryFee != null ? [{ label: 'Delivery Fee', value: order.deliveryFee > 0 ? `KSh ${order.deliveryFee.toLocaleString()}` : 'Free' }] : []),
                  ...(order.estimatedTotal != null ? [{ label: 'Total', value: `KSh ${(order.estimatedTotal + (order.deliveryFee || 0)).toLocaleString()}` }] : []),
                  { label: 'Payment', value: `${order.paymentMethod === 'pay_before' ? 'Pay before delivery' : 'Pay on delivery'} · ${order.paymentStatus === 'paid' ? 'Paid' : 'Not yet paid'}` },
                  ...(order.assignedStoreName ? [{ label: 'Assigned Store', value: order.assignedStoreName }] : []),
                  ...(order.adminNotes ? [{ label: 'Notes', value: order.adminNotes }] : []),
                ].map(row => (
                  <div key={row.label} className="flex justify-between py-2 text-sm">
                    <span className="text-gray-500">{row.label}</span>
                    <span className="font-medium text-gray-900 text-right">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Items */}
            <div className="bg-white rounded-2xl shadow-sm p-5">
              <h3 className="font-semibold text-gray-800 mb-3">Items Ordered</h3>
              <div className="space-y-2">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl text-sm">
                    <span className="font-medium">{item.onionType} – {item.onionSize}</span>
                    <span className="text-gray-600 font-semibold">{item.quantityKg} kg</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Order again — reorder workflow for repeat customers */}
            {order.status === 'delivered' && (
              <a href="/order" className="flex items-center justify-between gap-3 bg-emerald-900 hover:bg-emerald-950 text-white rounded-2xl shadow-sm p-5 transition-colors">
                <div>
                  <p className="font-bold">Need more onions?</p>
                  <p className="text-emerald-200 text-sm mt-0.5">Reorder in seconds — your details are saved.</p>
                </div>
                <span className="shrink-0 bg-white text-emerald-900 font-bold text-sm px-4 py-2.5 rounded-xl">Order again</span>
              </a>
            )}

            {/* Quality issue — only after delivery */}
            {order.status === 'delivered' && (
              <div className="bg-white rounded-2xl shadow-sm p-5">
                {claim ? (
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={18} className="text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Quality issue reported</p>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {QUALITY_ISSUE_LABELS[claim.issueType]} — <span className="font-medium text-amber-700">{QUALITY_CLAIM_LABELS[claim.status]}</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-1">We'll call you to arrange a replacement or refund.</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Not happy with the onions?</p>
                      <p className="text-xs text-gray-500 mt-0.5">We replace or refund substandard produce — no questions asked.</p>
                    </div>
                    <button
                      onClick={() => setShowClaimModal(true)}
                      className="shrink-0 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors"
                    >
                      Report an issue
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* CTA for new order */}
        {(!searched || !order) && !loading && (
          <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
            <Package size={28} className="mx-auto text-emerald-700 mb-3" />
            <h3 className="font-bold text-gray-800 mb-2">Want to place an order?</h3>
            <p className="text-gray-600 text-sm mb-4">Fresh red onions delivered across Nairobi. Pay on delivery.</p>
            <a
              href="/order"
              className="inline-block px-6 py-3 bg-emerald-900 hover:bg-emerald-950 text-white font-bold rounded-xl transition-colors"
            >
              Place an Order
            </a>
          </div>
        )}
      </div>

      {showClaimModal && order && (
        <QualityClaimModal
          order={order}
          onClose={() => setShowClaimModal(false)}
          onSubmitted={(c) => { setClaim(c); setShowClaimModal(false); }}
        />
      )}
    </div>
  );
};

// ── Consumer quality-issue report modal ──────────────────────────────────────
const ISSUE_TYPES: QualityIssueType[] = ['bruised', 'overripe', 'wrong_grade', 'damaged', 'short_weight', 'other'];

const QualityClaimModal: React.FC<{
  order: TrackedOrder;
  onClose: () => void;
  onSubmitted: (claim: QualityClaim) => void;
}> = ({ order, onClose, onSubmitted }) => {
  const [issueType, setIssueType] = useState<QualityIssueType>('bruised');
  const [description, setDescription] = useState('');
  const [resolution, setResolution] = useState<ResolutionMethod>('either');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (description.trim().length < 5) { alert('Please describe the issue.'); return; }
    setSaving(true);
    try {
      // Upload any attached photos first (non-blocking on failure)
      let photoUrls: string[] | undefined;
      if (files.length) {
        try {
          photoUrls = await Promise.all(files.map(async (f, i) => {
            const r = storageRef(storage, `quality_claims/${order.trackingCode}-${i}-${f.name.replace(/[^\w.\-]/g, '_')}`);
            await uploadBytes(r, f);
            return getDownloadURL(r);
          }));
        } catch (upErr) {
          console.error('Photo upload failed, submitting without photos:', upErr);
        }
      }
      const id = await createQualityClaim({
        orderId: order.id,
        trackingCode: order.trackingCode,
        customerName: order.customerName,
        customerPhone: order.customerPhone,
        issueType,
        description: description.trim(),
        ...(photoUrls && photoUrls.length ? { photoUrls } : {}),
        preferredResolution: resolution,
      });
      onSubmitted({
        id, orderId: order.id, trackingCode: order.trackingCode,
        customerName: order.customerName, customerPhone: order.customerPhone,
        issueType, description: description.trim(), photoUrls, preferredResolution: resolution,
        status: 'pending', createdAt: new Date(), updatedAt: new Date(),
      });
    } catch (e) {
      console.error(e);
      alert('Could not submit. Please call us on +254 790 215 267.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-gray-900 text-lg">Report a quality issue</h3>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <p className="text-xs text-gray-500 mb-4">Order {order.trackingCode}</p>

        <label className="text-xs font-semibold text-gray-500 uppercase">What's wrong?</label>
        <select value={issueType} onChange={e => setIssueType(e.target.value as QualityIssueType)}
          className="mt-1 mb-3 w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm bg-white text-gray-900">
          {ISSUE_TYPES.map(t => <option key={t} value={t}>{QUALITY_ISSUE_LABELS[t]}</option>)}
        </select>

        <label className="text-xs font-semibold text-gray-500 uppercase">Tell us more</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
          placeholder="e.g. about 3 kg were soft and starting to rot"
          className="mt-1 mb-3 w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm bg-white text-gray-900 resize-none" />

        <label className="text-xs font-semibold text-gray-500 uppercase">Preferred resolution</label>
        <div className="mt-1 mb-4 grid grid-cols-3 gap-2">
          {([['replacement', 'Replace'], ['refund', 'Refund'], ['either', 'Either']] as [ResolutionMethod, string][]).map(([val, label]) => (
            <button key={val} onClick={() => setResolution(val)}
              className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors ${resolution === val ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600'}`}>
              {label}
            </button>
          ))}
        </div>

        <label className="text-xs font-semibold text-gray-500 uppercase">Photos (optional, up to 2)</label>
        <input type="file" accept="image/*" multiple
          onChange={e => setFiles(Array.from(e.target.files || []).slice(0, 2))}
          className="mt-1 mb-4 w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-amber-50 file:text-amber-700 file:font-semibold" />
        {files.length > 0 && <p className="text-xs text-gray-400 -mt-3 mb-3">{files.length} photo{files.length > 1 ? 's' : ''} attached</p>}

        <button onClick={submit} disabled={saving}
          className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white font-bold rounded-xl">
          {saving ? 'Submitting…' : 'Submit report'}
        </button>
      </div>
    </div>
  );
};

export default TrackOrderPage;
